import { useState, useEffect } from 'react';
import { ejercicioService } from '../services/ejercicioService';
import { useUIStore } from '../store/uiStore';
import { Ejercicio } from '../types/ejercicio';
import {
  Heart,
  Clock,
  TrendingUp,
  Flame,
  Target
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EstadoVacio, EnlaceAlternativo } from '../components/EstadoVacio';
import { BeneficiosEjercicios, ModalBeneficios } from '../components/ejercicios/BeneficiosEjercicios';

/**
 * Listado de ejercicios.
 *
 * Pulsar un boton de duracion NO empieza el ejercicio aqui: navega a
 * `/ejercicios/:id?min=N`, y es en esa pantalla donde el circulo ofrece
 * "Iniciar ejercicio". Antes la sesion se pintaba en esta misma pagina y el
 * ejercicio ya venia corriendo al abrirla (el boton de la lista iniciaba el
 * conteo), y al volver atras con el gesto del movil la sesion se perdia sin
 * avisar.
 *
 * Esta pagina queda como lo que es: la lista. Ni cronometro, ni audio, ni fases.
 * Esos viven en `EjercicioDetallePage`.
 */
export default function EjerciciosPage() {
  const navigate = useNavigate();
  const { addToast } = useUIStore();
  const [ejercicios, setEjercicios] = useState<Ejercicio[]>([]);
  const [racha, setRacha] = useState(0);
  const [tiempoHoy, setTiempoHoy] = useState({ tiempoMinutos: 0, tiempoRestante: 30 });
  const [isLoading, setIsLoading] = useState(true);
  /** Al entrar se muestran los beneficios en un modal (la tarjeta del pie se conserva). */
  const [beneficiosAbiertos, setBeneficiosAbiertos] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  /**
   * Los beneficios se muestran al entrar, pero SOLO si hay algo que celebrar:
   * con la lista vacía lo primero que debe leer el estudiante es "todavía no hay
   * ejercicios publicados", y un modal encima se lo tapa (y además celebra
   * beneficios de ejercicios que no existen).
   */
  useEffect(() => {
    if (!isLoading && ejercicios.length > 0) {
      setBeneficiosAbiertos(true);
    }
  }, [isLoading, ejercicios.length]);

  const loadData = async () => {
    try {
      const [ejerciciosData, rachaData, tiempoData] = await Promise.all([
        ejercicioService.getEjercicios(),
        ejercicioService.getRacha(),
        ejercicioService.getTiempoHoy(),
      ]);

      setEjercicios(ejerciciosData);
      setRacha(rachaData.rachaDias);
      setTiempoHoy(tiempoData);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudieron cargar los ejercicios',
      });
    } finally {
      setIsLoading(false);
    }
  };

  /** La duracion viaja en la query: es la eleccion del boton, no del ejercicio. */
  const abrirEjercicio = (ejercicio: Ejercicio, minutos: number) => {
    navigate(`/ejercicios/${ejercicio.id}?min=${minutos}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner h-12 w-12"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Ejercicios de Bienestar</h1>
        <p className="text-v-on-surface-variant">
          Técnicas de respiración y relajación para tu bienestar mental
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          icon={Flame}
          label="Racha"
          value={`${racha} días`}
          color="text-v-ambar-700"
          bgColor="bg-v-ambar-100"
        />
        <StatCard
          icon={Clock}
          label="Tiempo hoy"
          value={`${tiempoHoy.tiempoMinutos} min`}
          color="text-v-azul-700"
          bgColor="bg-v-azul-100"
        />
        <StatCard
          icon={TrendingUp}
          label="Restante"
          value={`${tiempoHoy.tiempoRestante} min`}
          color="text-v-verde-700"
          bgColor="bg-v-verde-100"
        />
      </div>

      {/* Exercise Grid. Sin ejercicios la API devuelve `[]` (tabla vacia en la
          BD): antes se pintaba una rejilla en blanco y el estudiante leia
          "la app esta rota". */}
      {ejercicios.length === 0 ? (
        <EstadoVacio
          icono={Heart}
          container="#FCE7F3"
          ink="#9D174D"
          titulo="Todavía no hay ejercicios publicados"
          descripcion="El equipo está preparando las actividades de respiración. Vuelve en un rato y las verás aquí."
          alternativas={
            <EnlaceAlternativo
              icono={Target}
              texto="Volver al inicio"
              onClick={() => navigate('/dashboard')}
            />
          }
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {ejercicios.map((ejercicio) => (
            <ExerciseCard
              key={ejercicio.id}
              ejercicio={ejercicio}
              onStart={(duracion) => abrirEjercicio(ejercicio, duracion)}
              disabled={tiempoHoy.tiempoRestante <= 0}
            />
          ))}
        </div>
      )}

      {/* Tips. El degradado usaba `purple-50 -> pink-50`, que no cambia con el tema:
            en oscuro el `text-v-violeta-700` se aclara y quedaba a 1.62:1 sobre
            ese fondo claro. Con `bg-v-violeta-100` el par container/700 vuelve a
            hacer su trabajo. Se conserva tal cual, ademas del modal de entrada. */}
      <BeneficiosEjercicios />

      <ModalBeneficios open={beneficiosAbiertos} onClose={() => setBeneficiosAbiertos(false)} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bgColor }: any) {
  return (
    <div className={`${bgColor} rounded-xl p-4 border`}>
      <Icon className={`h-5 w-5 ${color} mb-2`} />
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs text-v-on-surface-variant">{label}</p>
    </div>
  );
}

interface ExerciseCardProps {
  ejercicio: Ejercicio;
  onStart: (duracion: number) => void;
  disabled: boolean;
}

function ExerciseCard({ ejercicio, onStart, disabled }: ExerciseCardProps) {
  const handleStart = (duracion: number) => {
    if (!disabled) {
      onStart(duracion);
    }
  };

  return (
    <div className="bg-card rounded-xl p-6 border hover:shadow-lg transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="bg-primary/10 rounded-full p-3">
          <Heart className="h-6 w-6 text-primary" />
        </div>
        <span className="text-xs bg-secondary px-2 py-1 rounded-full">
          {ejercicio.tipo}
        </span>
      </div>

      <h3 className="text-xl font-semibold mb-2">{ejercicio.titulo}</h3>
      <p className="text-sm text-v-on-surface-variant mb-4">
        {ejercicio.descripcion}
      </p>

      <div className="flex items-center space-x-2 text-sm text-v-on-surface-variant mb-4">
        <Clock className="h-4 w-4" />
        <span>{ejercicio.duracionMinima} - {ejercicio.duracionMaxima} min</span>
      </div>

      <div className="space-y-2">
        {/*
          Botones de duracion, no de arranque. El texto lo dice ("Comenzar
          (3 min)") y por eso el destino es la pantalla del ejercicio, donde
          el circulo ofrece "Iniciar ejercicio": quien decide cuando empezar es
          el usuario, no la tarjeta.
        */}
        <button
          onClick={() => handleStart(ejercicio.duracionMinima)}
          disabled={disabled}
          className="w-full py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
        >
          {disabled ? 'Límite diario alcanzado' : `Comenzar (${ejercicio.duracionMinima} min)`}
        </button>

        <div className="flex space-x-2">
          <button
            onClick={() => handleStart(Math.ceil((ejercicio.duracionMinima + ejercicio.duracionMaxima) / 2))}
            disabled={disabled}
            className="flex-1 py-2 bg-secondary rounded-lg font-medium hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {Math.ceil((ejercicio.duracionMinima + ejercicio.duracionMaxima) / 2)} min
          </button>
          <button
            onClick={() => handleStart(ejercicio.duracionMaxima)}
            disabled={disabled}
            className="flex-1 py-2 bg-secondary rounded-lg font-medium hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {ejercicio.duracionMaxima} min
          </button>
        </div>
      </div>
    </div>
  );
}